import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import cx from 'classnames';
import { Field, List, ListItem } from '@groupelacentrale/contact-storybook';
import { IconChevron } from '@groupelacentrale/contact-storybook/lib/icons';
import './FieldSearch.scss';
import useClickOutside from 'hooks/use-click-outside';
import strings from 'utils/strings';

interface Props {
  name: string;
  value?: string;
  options: string[];
  eligibleOptions?: string[];
  isDropdownMode?: boolean;
  onSelect: (option: string) => void;
  errorMessage?: string;
  [x: string]: unknown;
}

const filterStr = (options: string[], searchValue: string) => {
  const normalizedSearchValue = strings.normalize(searchValue);
  return options.filter(option => strings.normalize(option).startsWith(normalizedSearchValue));
};

const Search: React.ForwardRefRenderFunction<HTMLDivElement, Props> = (
  { name, value, options, eligibleOptions, isDropdownMode, onSelect, errorMessage, ...fieldProps },
  initialRef,
) => {
  const [searchValue, setSearchValue] = useState('');
  const [expanded, setExpanded] = useState(false);
  const [showAllOptions, setShowAllOptions] = useState(true);
  const ref = useRef(null);

  const filteredOptions = useMemo(
    () => (isDropdownMode && showAllOptions ? options : filterStr(options, searchValue)),
    [showAllOptions, options, searchValue, isDropdownMode],
  );

  const handleChange = useCallback(e => {
    setShowAllOptions(false);
    setSearchValue(e.currentTarget.value);
  }, []);

  const handleValue = useCallback(
    (option: string, resetValue = false) => {
      if (!resetValue && option && option.length > 0) onSelect(option);
      setSearchValue(option ?? '');
      setShowAllOptions(true);
      setExpanded(false);
    },
    [onSelect],
  );

  const pickFirstElement = useCallback(() => {
    if (expanded) {
      if (searchValue.length === 0) handleValue(value ?? '');
      if (searchValue.length > 0 && !filteredOptions.includes(searchValue)) handleValue(filteredOptions[0] ?? value);
    }
    setExpanded(false);
  }, [searchValue, filteredOptions, handleValue, expanded, value]);

  const handleFocus = useCallback(() => {
    setShowAllOptions(true);
    setExpanded(true);
  }, []);

  const handleClick = useCallback(e => handleValue(e.currentTarget.dataset.option ?? ''), [handleValue]);

  useClickOutside(initialRef ? (initialRef as React.RefObject<HTMLElement>) : ref, pickFirstElement);

  useEffect(() => {
    if (searchValue !== value && searchValue.length > 0 && filteredOptions.length > 0 && !expanded) setExpanded(true);
  }, [searchValue, filteredOptions.length, expanded, value]);

  useEffect(() => handleValue(value ?? '', true), [handleValue, value]);

  return (
    <div className="cbm-fieldsearch" ref={initialRef || ref}>
      <Field
        name={name}
        value={searchValue}
        onChange={handleChange}
        className={cx({ expanded: expanded && filteredOptions.length > 0 })}
        autoComplete="off"
        onFocus={handleFocus}
        errorMessage={expanded ? undefined : errorMessage}
        endAdornment={
          <div role="none" style={{ cursor: 'pointer' }} onClick={expanded ? pickFirstElement : handleFocus}>
            <IconChevron width={24} rotation={expanded ? 90 : 270} />
          </div>
        }
        {...fieldProps}
      />
      {expanded && filteredOptions.length > 0 && (
        <div className="list-container">
          <List>
            {filteredOptions.map((option: string, index: number) => (
              <ListItem tabIndex={index} key={option} data-option={option} onClick={handleClick}>
                <span className={cx({ 'not-eligible': eligibleOptions && !eligibleOptions.includes(option) })}>
                  {option}
                </span>
              </ListItem>
            ))}
          </List>
        </div>
      )}
    </div>
  );
};

const FieldSearch = React.forwardRef(Search);

export default FieldSearch;
